/**
 * App activity, as plain booleans. The ONE place in the app that listens to scroll.
 *
 * - `scrolling`: any element scrolled in the last ~140 ms. One passive capture listener on
 *   `document` that writes a single timestamp (no React state, no DOM writes, no allocation);
 *   one timeout (never re-created per event) flips the flag off. `scrollend` ends it sooner
 *   where supported, the timeout stays as the fallback.
 * - `interacting`: a pointer/touch is down, or a key was pressed in the last ~250 ms.
 * - `sheetOpen`: set by the app shell via `activity.setSheetOpen()`.
 * - `idle`: no input at all (pointer move, key, wheel, touch, scroll) for IDLE_MS. The sky holds its
 *   last frame, so a page nobody is touching costs the GPU nothing. Any input wakes it.
 * - `quiet`: none of the above. The sky pauses unless it is quiet.
 *
 * Dependency-free on purpose: no app stores, no React. Consumers `subscribe` and read
 * `getSnapshot()` (a new object only when a flag changes, so it works with useSyncExternalStore).
 */

export interface ActivitySnapshot {
  readonly scrolling: boolean
  readonly interacting: boolean
  readonly sheetOpen: boolean
  readonly idle: boolean
  readonly quiet: boolean
}

const SCROLL_IDLE_MS = 140
const POINTER_RELEASE_MS = 250
/** The entrance (sky arriving, first frames) plays; then, with no input, the sky stands still. */
export const IDLE_MS = 6000

let scrolling = false
let interacting = false
let sheetOpen = false
let idle = false
let lastResizeAt = -Infinity
let snapshot: ActivitySnapshot = { scrolling, interacting, sheetOpen, idle, quiet: true }
const listeners = new Set<() => void>()

function publish(): void {
  if (snapshot.scrolling === scrolling && snapshot.interacting === interacting && snapshot.sheetOpen === sheetOpen && snapshot.idle === idle) return
  snapshot = { scrolling, interacting, sheetOpen, idle, quiet: !scrolling && !interacting && !sheetOpen && !idle }
  for (const l of [...listeners]) l()
}

export const activity = {
  get lastResizeAt(): number { return lastResizeAt },
  subscribe(cb: () => void): () => void {
    listeners.add(cb)
    return () => {
      listeners.delete(cb)
    }
  },
  getSnapshot(): ActivitySnapshot {
    return snapshot
  },
  setSheetOpen(open: boolean): void {
    sheetOpen = open
    publish()
  },
  /** Something the viewer did or needs to see (a place switch): the sky is awake for another IDLE_MS. */
  wake(): void {
    wakeNow?.()
  },
}

// ── listeners ──────────────────────────────────────────────────────────────────

let wakeNow: (() => void) | null = null
let installs = 0
let uninstallListeners: (() => void) | null = null

function wire(): () => void {
  let lastScroll = 0
  let scrollTimer = 0
  let releaseTimer = 0
  let pointers = 0
  let lastInput = performance.now()
  let idleTimer = 0

  // Idle detection: one timestamp write per input event, one timer re-armed only when it fires.
  const idleWatch = () => {
    idleTimer = 0
    const remaining = IDLE_MS - (performance.now() - lastInput)
    if (remaining > 40) {
      idleTimer = window.setTimeout(idleWatch, remaining)
      return
    }
    idle = true
    publish()
  }
  const onInput = () => {
    lastInput = performance.now()
    if (idle) {
      idle = false
      publish()
    }
    if (!idleTimer) idleTimer = window.setTimeout(idleWatch, IDLE_MS)
  }
  wakeNow = onInput
  idleTimer = window.setTimeout(idleWatch, IDLE_MS)

  const idleCheck = () => {
    scrollTimer = 0
    const remaining = SCROLL_IDLE_MS - (performance.now() - lastScroll)
    if (remaining > 4) {
      scrollTimer = window.setTimeout(idleCheck, remaining)
      return
    }
    scrolling = false
    publish()
  }
  // The hot path: flag on (a no-op after the first event), a timestamp, and one timer if none is armed.
  const onScroll = () => {
    lastScroll = performance.now()
    onInput()
    if (scrolling) return
    scrolling = true
    publish()
    if (!scrollTimer) scrollTimer = window.setTimeout(idleCheck, SCROLL_IDLE_MS)
  }
  const onScrollEnd = () => {
    if (!scrolling) return
    window.clearTimeout(scrollTimer)
    scrollTimer = 0
    scrolling = false
    publish()
  }
  const onDown = () => {
    onInput()
    pointers++
    window.clearTimeout(releaseTimer)
    releaseTimer = 0
    if (interacting) return
    interacting = true
    publish()
  }
  const onUp = () => {
    pointers = Math.max(0, pointers - 1)
    if (pointers > 0) return
    window.clearTimeout(releaseTimer)
    releaseTimer = window.setTimeout(() => {
      releaseTimer = 0
      interacting = false
      publish()
    }, POINTER_RELEASE_MS)
  }
  const onKey = () => {
    const changed = !interacting
    interacting = true
    onInput()
    if (changed) publish()
    if (!pointers) onUp()
  }

  const opts = { capture: true, passive: true } as const
  const doc = document
  const onResize = () => { lastResizeAt = performance.now() }
  window.addEventListener('resize', onResize, { passive: true })
  doc.addEventListener('scroll', onScroll, opts)
  doc.addEventListener('scrollend', onScrollEnd, opts)
  doc.addEventListener('pointerdown', onDown, opts)
  doc.addEventListener('pointerup', onUp, opts)
  doc.addEventListener('pointercancel', onUp, opts)
  doc.addEventListener('pointermove', onInput, opts)
  doc.addEventListener('keydown', onKey, opts)
  doc.addEventListener('wheel', onInput, opts)
  doc.addEventListener('touchstart', onInput, opts)
  return () => {
    window.removeEventListener('resize', onResize)
    lastResizeAt = -Infinity
    doc.removeEventListener('scroll', onScroll, opts)
    doc.removeEventListener('scrollend', onScrollEnd, opts)
    doc.removeEventListener('pointerdown', onDown, opts)
    doc.removeEventListener('pointerup', onUp, opts)
    doc.removeEventListener('pointercancel', onUp, opts)
    doc.removeEventListener('pointermove', onInput, opts)
    doc.removeEventListener('keydown', onKey, opts)
    doc.removeEventListener('wheel', onInput, opts)
    doc.removeEventListener('touchstart', onInput, opts)
    window.clearTimeout(scrollTimer)
    window.clearTimeout(releaseTimer)
    window.clearTimeout(idleTimer)
    wakeNow = null
    scrolling = false
    interacting = false
    idle = false
    publish()
  }
}

/**
 * Wire the scroll and pointer listeners. Idempotent and reference-counted: every caller gets an
 * uninstall function, and the listeners go when the last one is called.
 */
export function installActivity(): () => void {
  if (typeof document === 'undefined') return () => {}
  if (installs++ === 0) uninstallListeners = wire()
  let done = false
  return () => {
    if (done) return
    done = true
    if (--installs === 0) {
      uninstallListeners?.()
      uninstallListeners = null
    }
  }
}
